import React from 'react';
import { Pencil, X, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

/**
 * RenameMealDialog — extracted from `pages/Meals.js` (Iter. 405.f).
 *
 * Pure presentational component. The parent owns the rename mutation
 * (API call, toast, meals[] state) and just hands us:
 *   • `open` / `onClose` — visibility control
 *   • `value` / `onChange` — controlled text input
 *   • `saving` — disables the Save button while the mutation runs
 *   • `onSave` — called when the user confirms (Enter or button)
 *   • `mealHasCustomName` — toggles the "Nom auto restauré" hint
 *   • `language` — drives the i18n labels
 *
 * Why extract:
 *   • `Meals.js` was >1330 lines. This dialog is ~85 lines, no shared
 *     state with the rest of the page, perfect candidate.
 *   • Easier to unit-test in isolation.
 */
export default function RenameMealDialog({
  open,
  onClose,
  value,
  onChange,
  saving,
  onSave,
  mealHasCustomName,
  language = 'fr',
  t,
}) {
  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) onClose();
      }}
    >
      <DialogContent className="bg-card border-border max-w-sm" data-testid="meal-rename-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-lg uppercase flex items-center gap-2">
            <Pencil className="w-4 h-4 text-pink-400" />
            {language === 'fr' ? 'Renommer le repas' : language === 'es' ? 'Renombrar la comida' : 'Rename meal'}
          </DialogTitle>
          <DialogDescription>
            {language === 'fr'
              ? 'Donne un nom personnalisé à ce repas (ex: « Cheat meal 30 mai »). Laisse vide pour restaurer le nom auto.'
              : language === 'es'
              ? 'Asigna un nombre personalizado (ej: « Cheat meal 30 mayo »). Déjalo vacío para restaurar el nombre automático.'
              : 'Give this meal a custom label (e.g. "Cheat meal May 30"). Leave empty to restore the auto name.'}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <label className="block text-xs uppercase tracking-wider text-muted-foreground">
            {language === 'fr' ? 'Nom personnalisé' : language === 'es' ? 'Nombre personalizado' : 'Custom name'}
          </label>
          <Input
            autoFocus
            value={value}
            onChange={(e) => onChange(e.target.value.slice(0, 60))}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !saving) {
                e.preventDefault();
                onSave();
              }
            }}
            placeholder={
              language === 'fr'
                ? 'Ex: Cheat meal 30 mai'
                : language === 'es'
                ? 'Ej: Cheat meal 30 mayo'
                : 'e.g. Cheat meal May 30'
            }
            maxLength={60}
            className="bg-secondary border-border"
            data-testid="meal-rename-input"
          />
          <div className="flex items-center justify-between text-[10px] uppercase tracking-wider text-muted-foreground">
            <span>{value.length}/60</span>
            {mealHasCustomName && !value.trim() && (
              <span className="text-pink-400">
                {language === 'fr'
                  ? '↩️ Nom auto restauré'
                  : language === 'es'
                  ? '↩️ Nombre auto restaurado'
                  : '↩️ Auto name restored'}
              </span>
            )}
          </div>
          <div className="flex gap-2 pt-2">
            <Button
              variant="outline"
              onClick={onClose}
              className="flex-1"
              data-testid="meal-rename-cancel-btn"
            >
              <X className="w-4 h-4 mr-2" /> {t ? t('meals_page.annuler', 'Annuler') : 'Annuler'}
            </Button>
            <Button
              onClick={onSave}
              disabled={saving}
              className="flex-1 bg-pink-500 hover:bg-pink-600 text-white"
              data-testid="meal-rename-save-btn"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Pencil className="w-4 h-4 mr-2" />
              )}
              {language === 'fr' ? 'Enregistrer' : language === 'es' ? 'Guardar' : 'Save'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
